import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDashboardData } from '@/services/dashboardDataService';
import { useTenancy } from '@/contexts/TenancyContext';
import "./widgets.css";
import { cleanDateAndTimeObject, toLocalDateString } from '@shared/renderDate';

interface UpcomingTask {
  id: string;
  description: string;
  dueDate: string | Date;
  checkDetail?: {
    check: string;
  };
  assID?: string;
}

const UpcomingTasksWidgetMediumTall: React.FC = () => {
  const { data, isLoading, error, metadata } = useDashboardData('upcoming-tasks');
  const { activeTenancy } = useTenancy();
  const [tasks, setTasks] = useState<UpcomingTask[]>([]);

  // Transform the data when it's loaded
  useEffect(() => {
    if (data?.upcoming) {
      const formattedTasks = data.upcoming.map((task: any) => ({
        id: task.id || task.assID || `task-${Math.random().toString(36).substr(2, 9)}`,
        title: task.checkDetail?.check || 'Task',
        dueDate: task.dueDate?.toDate ? task.dueDate.toDate() : cleanDateAndTimeObject(task.dueDate || Date.now()),
        description: task.description || '',
        status: 'pending',
      }));
      
      setTasks(formattedTasks);
    }
  }, [data]);

  const handleTaskClick = (assID: string) => {
    window.location.href = `assurance-actions.html?assID=${assID}`;
  };

  if (isLoading) {
    return (
      <CardContent className="h-full w-full flex flex-col" style={{ containerType: 'inline-size' }}>
        <div className="w-full flex justify-between items-center mb-2 flex-shrink-0">
          <h3 className="text-lg font-semibold truncate pr-2">Upcoming Tasks</h3>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center">
          <p className="text-sm text-muted-foreground">Loading tasks...</p>
        </div>
      </CardContent>
    );
  }

  if (error || metadata?.error) {
    return (
      <CardContent className="h-full w-full flex flex-col" style={{ containerType: 'inline-size' }}>
        <div className="w-full flex justify-between items-center mb-2 flex-shrink-0">
          <h3 className="text-lg font-semibold truncate pr-2">Upcoming Tasks</h3>
          {metadata?.lastUpdated && (
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {cleanDateAndTimeObject(metadata.lastUpdated).toLocaleTimeString()}
            </span>
          )}
        </div>
        <div className="flex-1 flex flex-col items-center justify-center">
          <p className="text-sm text-red-500 text-center px-2">
            {error || metadata?.message || 'Failed to load tasks'}
          </p>
        </div>
      </CardContent>
    );
  }

  return (
    <CardContent className="h-full w-full flex flex-col" style={{ containerType: 'inline-size' }}>
      <div className="w-full flex justify-between items-center mb-2 flex-shrink-0">
        <h3 className="text-lg font-semibold truncate pr-2">Upcoming Tasks</h3>
        {metadata?.lastUpdated && (
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {cleanDateAndTimeObject(metadata.lastUpdated).toLocaleTimeString()}
          </span>
        )}
      </div>
      
      <div className="flex-1 overflow-hidden flex flex-col">
        {tasks.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-4">
            <h1 style={{ fontSize: "2rem" }}>🎉</h1>
            <p className="text-sm text-muted-foreground text-center px-2">
              No tasks in the next 30 days
            </p>
          </div>
        ) : (
          <div className="overflow-y-auto pr-1 -mr-1">
            <ul className="w-full flex flex-col gap-2">
              {tasks.map((task) => (
                <li
                  key={task.assID || task.id}
                  className="rounded-md border border-border bg-background p-2 cursor-pointer hover:bg-accent transition"
                  onClick={() => handleTaskClick(task.assID || task.id)}
                >
                  <div className="font-medium text-xs flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold truncate">
                        {task.description || 'Upcoming task'}
                      </span>
                    </div>
                    <div className="text-muted-foreground text-xs">
                      <span className="whitespace-nowrap">
                        Due: {new Date(task.dueDate).toLocaleDateString(undefined, { 
                          month: 'short', 
                          day: 'numeric' ,
                          year: 'numeric'
                        })}
                        {' '}
                        <span className="text-blue-600">
                          (in {Math.ceil((new Date(task.dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))}d)
                        </span>
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </CardContent>
  );
};

export default UpcomingTasksWidgetMediumTall;
